<template>
  <!-- A <form> only when it stands on its own (the chat drawer). On the ride
       information page this editor is nested inside a <b-form>, and a form
       inside a form is invalid HTML. -->
  <component
    :is="embedded ? 'form' : 'div'"
    class="trip-profile-editor"
    :class="{ 'trip-profile-editor--embedded': embedded }"
    @submit.prevent="onSave"
  >
    <div
      v-for="field in fields"
      :key="field"
      class="trip-profile-editor__row"
    >
      <label class="trip-profile-editor__label" :for="inputId(field)">
        {{ t(labelKey(field)) }}
        <span
          v-if="managerWritten(field)"
          class="trip-profile-editor__mine"
          :title="t('aiChatProfileSetByYou')"
        >&#128274; {{ t('aiChatProfileSetByYou') }}</span>
      </label>
      <input
        :id="inputId(field)"
        v-model="draft[field]"
        type="text"
        class="trip-profile-editor__input"
        :class="{ 'trip-profile-editor__input--mine': managerWritten(field) }"
        autocomplete="off"
        @input="markDirty(field)"
        @keydown.enter="onEnter"
      />
    </div>
    <!-- Hidden where the profile is saved by the page's own Save button (the
         ride information page); the chat drawer still saves on its own. -->
    <div v-if="showActions" class="trip-profile-editor__actions">
      <!-- Outside a form of its own, a submit button would submit the <b-form>
           this editor is nested in, so the click is what saves instead. -->
      <button
        :type="embedded ? 'submit' : 'button'"
        class="trip-profile-editor__btn trip-profile-editor__btn--primary"
        @click="onSaveClick"
      >
        {{ t('aiChatProfileSave') }}
      </button>
      <button type="button" class="trip-profile-editor__btn" @click="$emit('cancel')">
        {{ t('aiChatProfileCancel') }}
      </button>
    </div>
  </component>
</template>

<script>
import { translationMixin } from '@/mixins/translationMixin';
import {
  PROFILE_FIELD_ORDER,
  profileValues,
  isManagerWritten,
  profileFieldLabelKey,
} from '@/utils/tripProfile';

let instanceCounter = 0;

// Self-contained: takes a stored profile ({field: {value, source}}) and emits the
// flat {field: string} the save endpoint expects. Knows nothing of the chat panel.
export default {
  name: 'TripProfileEditor',
  mixins: [translationMixin],
  props: {
    profile: { type: Object, default: () => ({}) },
    embedded: { type: Boolean, default: false },
    // False where the surrounding page owns saving. The editor then emits
    // "change" on every keystroke instead, so the page can save the draft
    // with the rest of its form.
    showActions: { type: Boolean, default: true },
  },
  data() {
    instanceCounter += 1;
    return {
      uid: instanceCounter,
      fields: PROFILE_FIELD_ORDER,
      draft: this.seed(),
      dirty: {},
    };
  },
  watch: {
    // A fresher profile (e.g. an AI reply while the editor is open) refreshes
    // only the fields the manager has not touched.
    profile() {
      const values = profileValues(this.profile);
      PROFILE_FIELD_ORDER.forEach((f) => {
        if (!this.dirty[f]) this.$set(this.draft, f, values[f] || '');
      });
    },
  },
  methods: {
    seed() {
      const values = profileValues(this.profile);
      const out = {};
      PROFILE_FIELD_ORDER.forEach((f) => { out[f] = values[f] || ''; });
      return out;
    },
    labelKey: profileFieldLabelKey,
    inputId(field) {
      return `trip-profile-${this.uid}-${field}`;
    },
    managerWritten(field) {
      return isManagerWritten(this.profile, field);
    },
    // `dirty` starts empty, so a plain assignment adds a key Vue 2 cannot see
    // -- the profile() watcher would then keep overwriting a field the manager
    // is typing in.
    markDirty(field) {
      this.$set(this.dirty, field, true);
      // A page that owns saving needs the draft as it is typed; it has no
      // Save button of its own to collect it on.
      if (!this.showActions) this.$emit('change', this.payload());
    },
    // In a real form the submit event already saves; firing on the click too
    // would emit twice.
    onSaveClick() {
      if (!this.embedded) this.onSave();
    },
    // Enter saved the profile while this was a <form> of its own. Outside one
    // nothing listens for it, and the keystroke would reach the <b-form> this
    // editor is nested in instead.
    onEnter(event) {
      if (this.embedded) return;
      event.preventDefault();
      this.onSave();
    },
    payload() {
      const existing = profileValues(this.profile);
      const out = {};
      PROFILE_FIELD_ORDER.forEach((f) => {
        const value = (this.draft[f] || '').trim();
        // Only fields the manager touched or changed are sent: the backend upgrades
        // every submitted field to manager-owned, so re-sending untouched AI values
        // would silently lock them. A cleared field goes as '' (removes it); a field
        // that was never set and is still empty is left out.
        const touched = this.dirty[f] || value !== (existing[f] || '');
        if (touched && (value || existing[f])) out[f] = value;
      });
      return out;
    },
    onSave() {
      this.$emit('save', this.payload());
    },
  },
};
</script>

<style scoped>
.trip-profile-editor { display: flex; flex-direction: column; gap: 12px; }
.trip-profile-editor--embedded { padding: 16px; overflow-y: auto; }
.trip-profile-editor__row { display: flex; flex-direction: column; gap: 4px; }
.trip-profile-editor__label {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}
.trip-profile-editor__mine {
  font-size: 11px;
  font-weight: 400;
  color: #2979ff;
  background: #e8f0ff;
  border-radius: 10px;
  padding: 1px 8px;
}
.trip-profile-editor__input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
}
.trip-profile-editor__input--mine { border-color: #2979ff; }
.trip-profile-editor__actions { display: flex; gap: 8px; margin-top: 4px; }
.trip-profile-editor__btn {
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
}
.trip-profile-editor__btn--primary { background: #2979ff; border-color: #2979ff; color: #fff; }
</style>
